import axios from 'axios';
import React, { useEffect, useState, useRef } from 'react';
import { useUser } from '../../context/UserContext';
import { motion } from 'framer-motion';
import Pagination from "@mui/material/Pagination";
import Stack from "@mui/material/Stack";
import { IoEyeOutline, IoFilterSharp } from 'react-icons/io5';
import { useSearchParams } from 'react-router-dom';
import api from '../../utils/api';
import { useNavigate } from "react-router";
import { IoSearchSharp } from 'react-icons/io5';


function CompOffReports() {
  const [compOffs, setCompOffs] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [requestsPerPage] = useState(8);
  const [apiLoading, setApiLoading] = useState(false);
  const { currentUser, loading } = useUser();
  const baseUrl = process.env.REACT_APP_API_URL;
  const [searchParams] = useSearchParams();
  const [selectedReport, setSelectedReport] = useState(null);
  const [showViewModal, setShowViewModal] = useState(false);

  const [exportLoading, setExportLoading] = useState(false);

  const [draftStatus, setDraftStatus] = useState("ALL");
const [draftManager, setDraftManager] = useState("");
const [draftLocation, setDraftLocation] = useState("");

  const [status, setStatus] = useState("ALL");
  const [selectedManager, setSelectedManager] = useState("");
  const [selectedLocation, setSelectedLocation] = useState("");
  const [managers, setManagers] = useState([]);
  const [filterOpen, setFilterOpen] = useState(false);
  const filterRef = useRef(null);

  const activeFilterCount = [status !== "ALL" ? status : "", selectedManager, selectedLocation].filter(Boolean).length;
const [searchInput, setSearchInput] = useState(searchParams.get('search') || '');

const [selectedRows, setSelectedRows] = useState([]);

const navigate = useNavigate();

const handleSelectRow = (id) => {
  setSelectedRows(prev =>
    prev.includes(id) ? prev.filter(r => r !== id) : [...prev, id]
  );
};

const handleSelectAll = () => {
const allIds = compOffs.map(r => r.id);
  const allSelected = allIds.every(id => selectedRows.includes(id));
  setSelectedRows(allSelected ? [] : allIds);
};

  // Click outside to close filter
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (filterRef.current && !filterRef.current.contains(e.target)) {
        setFilterOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const fetchManagers = async () => {
    try {
      const response = await api.get('/auth/managers');
      setManagers(response.data || []);
    } catch (error) {
      console.error('Failed to fetch managers', error);
    }
  };

  useEffect(() => {
    fetchManagers();
  }, []);

const applySearch = () => {
  navigate(`?search=${searchInput}`);
  setCurrentPage(1);
};

const handleExport = async () => {
  setExportLoading(true);
  try {
    const params = {};

    if (selectedRows.length > 0) {
      params.ids = selectedRows.join(',');
    }

const response = await api.get(`/api/compoffs/export`, {
      params,
      responseType: "blob",
    });

    const blob = new Blob([response.data], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });

    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = "leave_reports.xlsx";
    link.click();
    URL.revokeObjectURL(link.href);

  } catch (err) {
    console.error("Export failed", err);
  } finally {
    setExportLoading(false);
  }
};

  const formatTiming = (timing) => {
  switch (timing) {
    case "FULL_DAY":
      return "Full Day";
    case "FIRST_HALF":
      return "First Half";
    case "SECOND_HALF":
      return "Second Half";
    default:
      return timing;
  }
};


  useEffect(() => {
    fetchCompOffReports(currentPage);
  }, [currentPage]);

  useEffect(() => {
    setCurrentPage(1);
    fetchCompOffReports(1);
  }, [searchParams, status, selectedManager, selectedLocation]);

  const fetchCompOffReports = async (page = 1, size = requestsPerPage) => {
  setApiLoading(true);
  try {
    const searchTerm = searchParams.get("search") || "";
    const response = await api.get(`/api/compoffs/comp-off-reports`, {
      params: {
        page: page - 1,
        size,
        search: searchTerm,
        status: status === "ALL" ? null : status,
        managerId: selectedManager || null,
        location: selectedLocation || null,
      }
    });
    const data = response.data;
    const mappedData = data.content.map(req => ({
      id: req.id,
      employeeName: req.employeeName,
      employeeId: req.employeeId,
      onDate: req.onDate,
      comOffStatus: req.comOffStatus,
      duration: req.duration,
      timing: req.timing,
      reason: req.reason,
      approverComment: req.approverComment,
      isUsed: req.isUsed,
      expiresOn: req.expiresOn,
      createdAt: req.createdAt,
      updatedAt: req.updatedAt
    }));
    setCompOffs(mappedData);
    setTotalPages(data.totalPages);
    setCurrentPage(data.currentPage + 1);
  } catch (error) {
    console.error('Failed to fetch Comp Off reports:', error);
  } finally {
    setApiLoading(false);
  }
};

  const formatDate = (dateStr) => {
    if (!dateStr) return "";
    const [year, month, day] = dateStr.split("-");
    return `${day}-${month}-${year}`;
  };

  const handleView = (report) => {
    setSelectedReport(report);
    setShowViewModal(true);
  };

  if (loading || !currentUser) {
    return <div className="p-6 text-center">Loading...</div>;
  }

  return (
    <div className="sm:p-6 sm:mt-auto mt-8 w-full mx-auto">
      <div className="flex flex-col sm:flex-row justify-between items-center gap-4 mt-1 mb-4 bg-white rounded-2xl p-4">
  <div>
    <h2 className="text-xl font-semibold text-slate-800">Comp Off Reports</h2>
    <p className="text-sm text-slate-400">{compOffs.length} records shown</p>
  </div>
        <div className="flex flex-1 sm:flex-none items-center gap-3 w-full sm:w-auto">
            {/* Search */}
<div className="relative">
  <input
    type="text"
    placeholder="Search employee..."
    value={searchInput}
    onChange={(e) => setSearchInput(e.target.value)}   // only updates local state
    onKeyDown={(e) => {
      if (e.key === 'Enter') applySearch();             // apply on Enter
    }}
    className="pl-9 pr-4 py-2 text-sm rounded-full border border-slate-200 bg-slate-50 focus:outline-none focus:border-slate-400 w-56"
  />
  <IoSearchSharp
    onClick={applySearch}                               // apply on icon click
    className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm cursor-pointer hover:text-slate-600"
  />
</div>

<button
  onClick={handleExport}
  disabled={selectedRows.length === 0 || exportLoading}
  className={`px-5 py-2.5 rounded-full text-xs font-medium transition ${
    selectedRows.length === 0
      ? "bg-slate-200 text-slate-400 cursor-not-allowed"
      : "bg-green-600 text-white hover:bg-green-700"
  }`}
>
  {exportLoading ? "Exporting..." : `Export (${selectedRows.length})`}
</button>

          {/* Filter Icon Button */}
          <div className="relative" ref={filterRef}>
            <button
              onClick={() => {
                if(!filterOpen) {
      // Sync draft to applied when opening
      setDraftStatus(status);
      setDraftManager(selectedManager);
      setDraftLocation(selectedLocation);
    }
    setFilterOpen(prev => !prev);
  }}
              className={`relative flex items-center gap-1.5 px-4 py-1.5 rounded-full border text-sm font-medium transition
                ${activeFilterCount > 0
                  ? 'border-slate-700 bg-slate-700 text-white'
                  : 'border-slate-300 bg-white text-slate-600 hover:border-slate-400'
                }`}
            >
              <IoFilterSharp className="text-base" />
              <span>Filter</span>
              {activeFilterCount > 0 && (
                <span className="ml-1 bg-white text-slate-700 text-[10px] font-bold rounded-full w-4 h-4 flex items-center justify-center">
                  {activeFilterCount}
                </span>
              )}
            </button>

            {/* Filter Dropdown Panel */}
            {filterOpen && (
              <div className="absolute right-0 mt-2 w-72 bg-white border border-slate-200 rounded-2xl shadow-xl z-50 p-4 space-y-4">
                
                <div className="flex items-center justify-between mb-1">
                  <span className="text-sm font-semibold text-slate-700">Filters</span>
                  {activeFilterCount > 0 && (
                    <button
                      onClick={() => {
                            setDraftStatus("ALL");
    setDraftManager("");
    setDraftLocation("");
                        setStatus("ALL");
                        setSelectedManager("");
                        setSelectedLocation("");
                        setCurrentPage(1);
                      }}
                      className="text-xs text-red-500 hover:text-red-600 font-medium"
                    >
                      Clear all
                    </button>
                  )}
                </div>

                {/* Status Filter */}
                <div>
                  <label className="block text-xs font-medium text-slate-500 mb-1.5">Status</label>
                  <select
                    value={draftStatus}
                    onChange={(e) => setDraftStatus(e.target.value)}
                    className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-slate-400"
                  >
                    <option value="ALL">All Status</option>
                    <option value="APPROVED">Approved</option>
                    <option value="PENDING">Pending</option>
                    <option value="REJECTED">Rejected</option>
                  </select>
                </div>

                {/* Manager Filter */}
                <div>
                  <label className="block text-xs font-medium text-slate-500 mb-1.5">Manager</label>
                  <select
                    value={draftManager}
                    onChange={(e) => setDraftManager(e.target.value)}
                    className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-slate-400"
                  >
            <option value="">All Managers</option>
            {managers.map((manager) => (
              <option key={manager.id} value={manager.id}>
                {manager.name}
              </option>
                    ))}
                  </select>
                </div>

                {/* Location Filter */}
<div>
  <label className="block text-xs font-medium text-slate-500 mb-1.5">Location</label>
  <div className="flex gap-2">
    {['Bengaluru', 'Mangaluru'].map((loc) => (
      <button
        key={loc}
        onClick={() => {
          setDraftLocation(prev => prev === loc ? '' : loc);
        }}
        className={`flex-1 py-1.5 rounded-lg text-xs font-medium border transition
          ${draftLocation === loc
            ? 'bg-slate-700 text-white border-slate-700'
            : 'bg-white text-slate-600 border-slate-200 hover:border-slate-400'
          }`}
      >
        {loc}
      </button>
    ))}
  </div>
</div>

                {/* Apply Button */}
                <button
                  onClick={() => {
                        setStatus(draftStatus);
    setSelectedManager(draftManager);
    setSelectedLocation(draftLocation);
                    setCurrentPage(1);
                    setFilterOpen(false);
                  }}
                  className="w-full bg-slate-700 text-white py-2 rounded-lg text-sm font-medium hover:bg-slate-600 transition"
                >
                  Apply Filters
                </button>
              </div>
            )}
          </div>


        </div>
      </div>

      <motion.div className="hidden sm:block bg-white rounded-2xl shadow-md overflow-hidden" initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="border-b border-slate-100">
              <tr>
                    <th className="px-3 py-4 text-left">
      <input
        type="checkbox"
        checked={compOffs.length > 0 && compOffs.every(r => selectedRows.includes(r.id))}
        onChange={handleSelectAll}
        className="w-4 h-4"
      />
    </th>
                <th className="px-3 py-4 text-sm text-left font-semibold text-slate-600">Sl. No</th>
                <th className="px-3 py-4 text-sm text-left font-semibold text-slate-600">Employee ID</th>
                <th className="px-3 py-4 text-sm text-left font-semibold text-slate-600">Employee Name</th>
                
                <th className="px-3 py-4 text-sm text-left font-semibold text-slate-600">On Date</th>
                <th className="px-3 py-4 text-sm text-left font-semibold text-slate-600">Timing</th>        
                <th className="px-3 py-4 text-sm text-center font-semibold text-slate-600">Status</th>
                <th className="px-3 py-4 text-sm text-center font-semibold text-slate-600">View</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {apiLoading ? (
                <tr>
                  <td colSpan="9" className="text-center py-8">Loading...</td>
                </tr>
              ) : compOffs.length === 0 ? (
                <tr>
                  <td colSpan="9" className="text-center py-8 text-slate-500">No comp off records found.</td>
                </tr>
              ) : (
                compOffs.map((report, index) => (
                  <tr key={report.id} className="hover:bg-slate-50">
                        <td className="px-3 py-2 align-middle">
      <input
        type="checkbox"
checked={selectedRows.includes(report.id)}
onChange={() => handleSelectRow(report.id)}
        className="w-4 h-4"
      />
    </td>
                    <td className="px-3 py-2 text-sm text-slate-600">{(currentPage - 1) * requestsPerPage + index + 1}</td>
                    <td className="px-3 py-2 text-sm">{report.employeeId}</td>
                    <td className="px-3 py-2 text-sm">{report.employeeName}</td>
                    
                    <td className="px-3 py-2 text-sm">{formatDate(report.onDate)}</td>
                    <td className="px-3 py-2 text-sm">{formatTiming(report.timing)}</td>
                    <td className="px-3 py-2 text-sm text-center">
                      <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                        report.comOffStatus === "APPROVED"
                          ? "bg-green-100 text-green-800"
                          : report.comOffStatus === "PENDING"
                          ? "bg-yellow-100 text-yellow-800"
                          : "bg-red-100 text-red-800"
                      }`}>
                        {report.comOffStatus}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-center">
                      <motion.button whileHover={{ scale: 1.1 }} whileTap={{ scale: 0.9 }} onClick={() => handleView(report)} className="text-blue-600 hover:text-blue-800">
                        <IoEyeOutline className="text-xl" />
                      </motion.button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>

          {compOffs.length > 0 && (
            <div className="px-6 py-4 border-t border-slate-100">
              <Stack spacing={2} className="flex justify-end">
                <Pagination
                  count={totalPages}
                  page={currentPage}
                  onChange={(_, page) => setCurrentPage(page)}
                  variant="outlined"
                  shape="full"
                  size="small"
                  showFirstButton
                  showLastButton
                   sx={{
                    "& .MuiPaginationItem-root": {
                      color: "#475569",
                      borderColor: "#e2e8f0",
                    },
                    "& .Mui-selected": {
                      backgroundColor: "#475569 !important",
                      color: "#fff",
                    },
                  }}
                />
              </Stack>
            </div>
          )}
        </div>
      </motion.div>

      {/* View Modal */}
      {showViewModal && selectedReport && (
        <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm z-50 flex items-center justify-center" onClick={(e) => { if (e.target === e.currentTarget) setShowViewModal(false); }}>
          <motion.div className="bg-white p-6 rounded-2xl max-w-xl w-full mx-4 shadow-xl relative" initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }}>
            <button onClick={() => setShowViewModal(false)} className="absolute top-4 right-4 text-slate-400 hover:text-red-500 transition">✕</button>
            <h2 className="text-xl font-semibold mb-4 text-center">Comp Off Details</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-slate-500">Employee Name</p>
                <p className="font-medium text-slate-800">{selectedReport.employeeName}</p>
              </div>
              <div>
                <p className="text-sm text-slate-500">Status</p>
                <p className="font-medium text-slate-800">{selectedReport.comOffStatus}</p>
              </div>
              <div>
                <p className="text-sm text-slate-500">On Date</p>
                <p className="font-medium text-slate-800">{formatDate(selectedReport.onDate)}</p>
              </div>
              <div>
                <p className="text-sm text-slate-500">Timing</p>
                <p className="font-medium text-slate-800">{formatTiming(selectedReport.timing)}</p>
              </div>
              <div>
                <p className="text-sm text-slate-500">Duration</p>
                <p className="font-medium text-slate-800">{selectedReport.duration} day(s)</p>
              </div>
              <div>
                <p className="text-sm text-slate-500">Used</p>
                <p className="font-medium text-slate-800">{selectedReport.isUsed ? "Yes" : "No"}</p>
              </div>
              <div className="sm:col-span-2">
                <p className="text-sm text-slate-500">Reason</p>
                <p className="font-medium text-slate-800 whitespace-pre-line">{selectedReport.reason}</p>
              </div>
              <div>
  <p className="text-sm text-slate-500">Applied On</p>
  <p className="font-medium text-slate-800">
    {selectedReport.createdAt ? new Date(selectedReport.createdAt).toLocaleDateString('en-GB', { day:'2-digit', month:'short', year:'numeric' }) : '—'}
  </p>
</div>
<div>
  <p className="text-sm text-slate-500">
    {selectedReport.comOffStatus === 'APPROVED' ? 'Approved Date' : selectedReport.comOffStatus === 'REJECTED' ? 'Rejected Date' : 'Edited Date'}
  </p>
  <p className="font-medium text-slate-800">
    {selectedReport.updatedAt ? new Date(selectedReport.updatedAt).toLocaleDateString('en-GB', { day:'2-digit', month:'short', year:'numeric' }) : '—'}
  </p>
</div>
              {selectedReport.expiresOn && (
                <div className="sm:col-span-2">
                  <p className="text-sm text-slate-500">Expires On</p>
                  <p className="font-medium text-slate-800">{formatDate(selectedReport.expiresOn)}</p>
                </div>
              )}
            </div>
            <div className="mt-6 text-center">
              <button onClick={() => setShowViewModal(false)} className="px-3 py-2 bg-slate-800 text-white rounded-full hover:bg-slate-700 transition">Close</button>
            </div>
          </motion.div>
        </div>
      )}




    </div>
  );
}

export default CompOffReports;
